import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import FocusTrap from 'focus-trap-react'
import shortid from 'shortid'
import './Modal.scss'
import PropTypes from 'prop-types'
import Portal from 'components/Portal/Portal'

export class Modal extends Component {
  static propTypes = {
    modalSize: PropTypes.string,
    headerType: PropTypes.string,
    title: PropTypes.string,
    cancelButton: PropTypes.bool,
    cancelButtonText: PropTypes.string,
    saveAndCloseButton: PropTypes.bool,
    saveButton: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    hideHeader: PropTypes.bool,
    noBackground: PropTypes.bool,
    modalStyle: PropTypes.object,
    modalBodyClassName: PropTypes.string,
    rootClassName: PropTypes.string,
    autoHeight: PropTypes.bool,
    closeButton: PropTypes.bool,
    bodyColor: PropTypes.string,
    save: PropTypes.func,
    containingElement: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }

  constructor(props) {
    super()
    this.state = {
      shadeAnimationClass: 'fadeIn',
      modalAnimationClass: props.disableEntranceAnimation ? '' : 'fadeInUp',
      closing: false,
    }
    this.id = shortid.generate()
  }
  closeModal = (callback, noSave) => {
    if (this.props.save && !noSave) {
      this.props.save()
    }
    this.setState({
      shadeAnimationClass: 'fadeOut',
      modalAnimationClass: 'fadeOutDown',
      closing: true,
    })
    this.closeCallback = callback
  }
  cancelModal = () => {
    if (this.props.cancel) {
      this.props.cancel()
    }
    this.setState({
      shadeAnimationClass: 'fadeOut',
      modalAnimationClass: 'fadeOutDown',
      closing: true,
    })
  }
  transitionOver = () => {
    if (this.state.closing) {
      this.setState({ closing: false })
      this.props.closeModal()
      if (typeof this.closeCallback === 'function') {
        this.closeCallback()
      }
    }
  }
  render() {
    const {
      modalSize = '',
      headerType = '',
      title,
      cancelButton,
      cancelButtonText = 'Cancel',
      saveAndCloseButton,
      saveButton,
      children,
      hideHeader,
      noBackground,
      modalStyle = {},
      modalBodyClassName = '',
      rootClassName,
      autoHeight,
      closeButton,
      bodyColor = '',
      save,
      containingElement,
    } = this.props

    const modalElement = (
      <div
        className={`modal-wrapper ${modalSize} ${rootClassName}`}
        aria-labelledby={`title-${this.id}`}
      >
        <FocusTrap
          focusTrapOptions={{ clickOutsideDeactivates: true }}
          className={`modal animated fast-animation ${
            this.state.modalAnimationClass
          } ${modalSize} ${noBackground ? ' no-background' : ''} ${modalBodyClassName} ${
            hideHeader ? 'hide-header' : ''
          } ${autoHeight ? 'auto-height' : ''} ${bodyColor}`}
          style={modalStyle}
          role="dialog"
        >
          {!hideHeader && (
            <div className={'header ' + headerType}>
              <span className="title" id={`title-${this.id}`}>
                {title}
              </span>
              <div className="button-group">
                {cancelButton && (
                  <button onClick={this.cancelModal} className="btn btn-primary">
                    <span className="text">{cancelButtonText}</span>
                  </button>
                )}
                {saveAndCloseButton && (
                  <button onClick={this.closeModal} className="btn btn-secondary">
                    <span className="text">Save & Close</span>
                  </button>
                )}
                {saveButton && (
                  <button
                    onClick={() => {
                      save(() => {
                        this.closeModal(undefined, true)
                      })
                    }}
                    className="btn btn-secondary"
                  >
                    <span className="text">Save & Close</span>
                  </button>
                )}
                {closeButton && (
                  <button className="close-button" onClick={this.closeModal} aria-label="Close">
                    <span className="icon icon-cross" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          )}
          {(hideHeader || noBackground) && (
            <button className="no-background-close" onClick={this.closeModal} aria-label="Close">
              <span aria-hidden="true" className="icon icon-cross" />
            </button>
          )}
          {typeof children === 'function' ? children({ closeModal: this.closeModal }) : children}
        </FocusTrap>
        <div
          className={'shade animated fast-animation ' + this.state.shadeAnimationClass}
          onAnimationEnd={this.transitionOver}
          onClick={this.cancelModal}
        />
      </div>
    )

    // if a containing element was specified, insert modal into the target element
    if (containingElement) {
      let targetParentElement = undefined

      if (typeof containingElement === 'string') {
        targetParentElement = document.querySelector(containingElement)
      } else if (typeof containingElement === 'object' && containingElement instanceof Node) {
        targetParentElement = containingElement
      }

      if (targetParentElement !== undefined && targetParentElement !== null) {
        return ReactDOM.createPortal(modalElement, targetParentElement)
      } else {
        console.error('The "containingElement" argument provided is invalid:', containingElement)
      }
    } else {
      return modalElement
    }
  }
}

export class ConfirmDialog extends Component {
  constructor() {
    super()
    this.inputRefs = {}
    this.buttonRefs = {}
  }
  componentDidMount = () => {
    const { inputs = [] } = this.props
    if (inputs.length > 0) {
      this.inputRefs.input0.focus()
    }
  }
  closeModal = (callback) => {
    this.modal.closeModal(() => {
      if (this.props.onModalClosed) {
        this.props.onModalClosed()
      }
      if(callback){
        callback()
      }
    })
  }
  getInputObject = () => {
    let values = {}
    Object.keys(this.inputRefs).forEach((ref, i) => {
      if (ref.includes('input')) {
        let value = this.inputRefs['input' + i].getValue()
        values[value.name] = value.value
      }
    })
    return values
  }
  submit = () => {
    const { buttons = [] } = this.props
    buttons.forEach((button, i) => {
      if (button.submit) {
        this.buttonRefs['button' + i].handleClick()
        return
      }
    })
  }
  render() {
    const {
      title,
      children,
      closeModal,
      message,
      buttons = [],
      inputs = [],
      sizeOverride,
      leftAligned,
      ...props
    } = this.props
    return (
      <Modal
        modalSize={sizeOverride || 'toast'}
        title={title}
        hideHeader
        closeModal={closeModal}
        ref={(modal) => {
          this.modal = modal
        }}
        {...props}
      >
        <FocusTrap
          focusTrapOptions={{ clickOutsideDeactivates: true }}
          className="modal-body"
          active={inputs.length > 0 || buttons.length > 0 ? undefined : false}
        >
          <span className="toast-message" style={leftAligned ? { textAlign: 'left' } : undefined}>
            {message}
          </span>
          {children}
          <div className="button-wrapper" style={{ paddingTop: 0 }}>
            {inputs.map((input, i) => (
              <DialogInput {...input} ref={(ref) => this.inputRefs['input' + i] = ref} submit={this.submit} key={i} />
            ))}
          </div>
          <div className="button-wrapper">
            {buttons.map((button, i) => (
              <DialogButton
                {...button}
                closeModal={this.closeModal}
                getInputObject={this.getInputObject}
                ref={(ref) => this.buttonRefs['button' + i] = ref}
                key={i}
              />
            ))}
          </div>
        </FocusTrap>
      </Modal>
    )
  }
}

export class DialogButton extends Component {
  handleClick = () => {
    if(this.props.fireAfterClose){
      this.props.closeModal(() => {
        this.clickEventHandler()
      })
    }else{
      this.clickEventHandler()
    }
  }
  clickEventHandler = () => {
    if (this.props.closeModal && !this.props.fireAfterClose) {
      this.props.closeModal()
    }
    if (this.props.customClickHandler) {
      if (this.props.onClick) {
        this.props.onClick()
      }
    } else {
      if (this.props.onClick) {
        this.props.onClick(this.props.getInputObject())
      }
    }
  }
  render() {
    const { label, className = 'btn-primary', disabled } = this.props
    return (
      <button disabled={disabled} className={'btn ' + className} onClick={this.handleClick}>
        {label}
      </button>
    )
  }
}

export class DialogInput extends Component {
  getValue = () => {
    return { name: this.props.name, value: this.input.value }
  }
  setValue = (value) => {
    this.input.value = value
  }
  focus = () => {
    this.input.focus()
  }
  checkForSubmit = ({ charCode }) => {
    if (charCode === 13) {
      this.props.submit()
    }
  }
  getFieldType = () => {
    const { type = 'text', className = '', style = {}, placeholder = '' } = this.props
    switch (type) {
      case 'text':
      default:
        return (
          <div className={'text-wrapper ' + className} style={style}>
            <input
              type="text"
              placeholder={placeholder}
              onKeyPress={this.checkForSubmit}
              ref={(input) => {
                this.input = input
              }}
            />
          </div>
        )
    }
  }
  render() {
    const { label = '' } = this.props
    return (
      <div className="field dialog-input">
        <label className="field-label">{label}</label>
        {this.getFieldType()}
      </div>
    )
  }
}

export const ControlledModal = ({
  isOpen: initialIsOpen,
  onModalClosed,
  onRequestModalClose,
  children,
  title,
  size = 'small',
  closeButton = true,
  autoHeight = true,
  noSaveOnClose = false,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = React.useState(initialIsOpen)
  const modal = React.useRef()

  React.useEffect(() => {
    if (initialIsOpen && !isOpen) {
      setIsOpen(true)
    } else if (!initialIsOpen && isOpen) {
      modal.current.closeModal(() => {
        setIsOpen(false)
      }, noSaveOnClose)
    }
  }, [initialIsOpen])

  return (
    <Portal isOpened={isOpen}>
      <Modal
        title={title}
        closeModal={() => {
          setIsOpen(false)
          onModalClosed()
        }}
        modalSize={size}
        closeButton={closeButton}
        autoHeight={autoHeight}
        ref={modal}
        {...restProps}
      >
        {children}
      </Modal>
    </Portal>
  )
}
ControlledModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClosed: PropTypes.func.isRequired,
  onRequestModalClose: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
}



// WEBPACK FOOTER //
// ./src/components/Modal/Modal.js